import { t, UUID, Money, DateTimeISO } from '../common.ts';

/**
 * There is deliberately no `DepositView` / `WithdrawView` here, and no status
 * union for either.
 *
 * They described a system that never shipped — `creditAmount`, `bonusType`,
 * `promotionId`, `matchedTxRef` and statuses `matched` / `credited` /
 * `auto_failed` on the deposit side, `processing`, `completedAt` and `note` on
 * the withdraw side, none of them columns of `finance.deposits` or
 * `finance.withdraws`. Nothing imported them, so nothing failed; they simply sat
 * here as a trap for whoever read them as the real contract.
 *
 * The accurate shapes are `DepositListItem` / `WithdrawListItem` in
 * `./finance-list.ts`, which svc-finance validates its own replies against.
 */
export const DepositMode = t.Union([
  t.Literal('copy'),
  t.Literal('gateway'),
  t.Literal('qr'),
  t.Literal('slip'),
]);

export const ManualDepositRequest = t.Object(
  {
    memberId: UUID,
    bankAccountId: UUID,
    amount: Money,
    mode: t.Union(
      [t.Literal('copy'), t.Literal('gateway'), t.Literal('qr'), t.Literal('slip')],
      { default: 'copy' },
    ),
    /**
     * A `finance.slip_uploads.id` returned by `POST /deposits/slips` — never a
     * URL. `POST /deposits` looks it up and refuses one that does not belong
     * to the resolved caller (the field used to be `slipUrl: t.String({
     * format: 'uri' })`, a caller-chosen address an approving operator's
     * browser would then open — a credential-harvesting / internal-network
     * SSRF vector. That field is REMOVED, not merely ignored: TypeBox strips
     * any `slipUrl` a caller still sends before this schema's handler ever
     * sees the body).
     */
    slipRef: t.Optional(UUID),
    promotionId: t.Optional(UUID),
    /**
     * The house account the money actually landed in — OPERATOR-ONLY, and
     * honoured only for an admin token.
     *
     * A player never names one, and svc-finance drops it from a player body
     * rather than trusting it: `bankAccountId` above is the MEMBER's own source
     * bank, and writing a caller-supplied id into the house column puts a
     * member-scoped (possibly foreign-tenant) id in a column joined against
     * `finance.bank_accounts`, which surfaced another tenant's account details on
     * the owning agent's queue.
     *
     * For a makeup row the operator is reading the bank statement, so they know
     * the destination before anyone approves it. It is checked against the
     * MEMBER's agent and its usage flag on the way in (FIN-304 / FIN-305), the
     * same two refusals the approve path makes, and the approving operator still
     * gets the last word.
     */
    houseBankAccountId: t.Optional(UUID),
    /**
     * A transfer into a house account with a currency of its own (LAK).
     * `originalAmount` is minor units of THAT account's currency, exactly what the
     * player is sending; `transferAccountId` names the account. svc-finance then
     * computes `amount` itself at the account's rate and stores all three, so the
     * figure a player was shown and the figure credited cannot drift apart. Sent
     * together or not at all; any `amount` beside them is ignored.
     */
    originalAmount: t.Optional(Money),
    transferAccountId: t.Optional(UUID),
    /**
     * Why staff are filing this deposit on the member's behalf — the makeup
     * remark.
     *
     * There is still deliberately no `note`. `finance.deposits` has no column
     * for one, so the field this schema used to accept was carried into an
     * insert that never named it: a 201 came back, nothing was stored, nothing
     * was logged, and no operator screen had anywhere to read one. `remark` is
     * NOT that field wearing a new name — it is not stored on the row either,
     * and it is not silently dropped: svc-finance writes it as the record's
     * first review event, in the same transaction as the insert, which is where
     * the colleague who has to decide it reads it (`apps/finance/src/routes/makeup.ts`).
     *
     * Optional here and REQUIRED in the handler for an admin caller (FIN-337).
     * TypeBox cannot express "required when the token is an admin's", and a
     * player filing their own deposit owes nobody a reason — narrowing this
     * shared shape would 422 every player call in the platform.
     */
    remark: t.Optional(t.String({ maxLength: 500 })),
  },
  { $id: 'ManualDepositRequest' },
);

export const SlipUploadResult = t.Object(
  {
    /** `finance.slip_uploads.id` — pass this back as `slipRef` on `POST /deposits`. */
    slipRef: UUID,
  },
  { $id: 'SlipUploadResult' },
);

export const SlipViewResult = t.Object(
  {
    /** Presigned GET URL. Time-limited — see `expiresAt`. Never log this value. */
    url: t.String(),
    expiresAt: DateTimeISO,
  },
  { $id: 'SlipViewResult' },
);

export const WithdrawRequest = t.Object(
  {
    amount: Money,
    memberBankId: UUID,
    /** No `note`, for the same reason as `ManualDepositRequest` — `finance.withdraws` has no column for one. */
  },
  { $id: 'WithdrawRequest' },
);

/**
 * Withdraw approve/reject.
 *
 * `reason` is the mandatory remark and is
 * REQUIRED, of every caller — the back office, an existing admin's script, a
 * blackbox actor. `minLength` refuses the empty string here; svc-finance refuses
 * a string of spaces (FIN-332), which TypeBox cannot express.
 *
 * A contract this shared cannot be narrowed ahead of the code that fills it in:
 * tightening a field to required has to land in one change with its own callers,
 * or it is a day of 422s.
 */
export const ApproveRejectRequest = t.Object(
  {
    action: t.Union([t.Literal('approve'), t.Literal('reject')]),
    reason: t.String({ minLength: 1, maxLength: 500 }),
  },
  { $id: 'ApproveRejectRequest' },
);

/**
 * Marking a withdrawal paid.
 *
 * `reason` is required for the same reason it is on the decision above: a
 * completion is the moment the payout becomes terminal and unmatched-forever if
 * it was wrong, so the record has to say who confirmed the transfer and on what
 * evidence. It is NOT limit-checked — the ceiling was applied at approval and
 * re-checking it here would strand an approved payout because the operator who
 * happens to be on shift has a smaller one.
 *
 * `houseBankAccountId` is optional at this layer on purpose, so an absent one
 * falls through to svc-finance's FIN-317 guard (a stable coded 400) rather than
 * a generic validation 422 that carries no code for the console to key a message
 * on. A present-but-malformed id still fails validation here.
 */
export const WithdrawCompleteRequest = t.Object(
  {
    reason: t.String({ minLength: 1, maxLength: 500 }),
    /**
     * Legacy: a caller-supplied URL. Kept because rows carry what was once sent
     * to it, but the back office no longer offers it — an operator attaching
     * proof uploads a file and names the reference below instead, which keeps
     * the image inside the platform's own private storage.
     */
    slipUrl: t.Optional(t.String({ format: 'uri' })),
    /**
     * A `finance.slip_uploads` id from `POST /withdraw-proofs/:id`. svc-finance
     * re-checks the row's `withdraw_id` against THIS withdrawal rather than
     * trusting the body, so neither another payout's proof nor the player's own
     * deposit slip — which shares the table, the brand and the member — can be
     * filed here. `POST /deposits` refuses the mirror image.
     */
    proofRef: t.Optional(UUID),
    houseBankAccountId: t.Optional(UUID),
  },
  { $id: 'WithdrawCompleteRequest' },
);

/**
 * Bulk withdrawal approval.
 *
 * One remark for the whole batch, required and stamped on EVERY review row it
 * writes — an operator who authorises twenty payouts in one click has given one
 * reason, and a trace that carried it on only the row that happened to fail
 * would answer "why was this approved" with a blank for the other nineteen.
 *
 * `ids` is what the operator actually reviewed (BO-03) and `expectedTotalMinor`
 * the total they were shown; svc-finance refuses the whole batch if either has
 * moved. Per-row LIMIT outcomes are the exception: a row over the caller's
 * ceiling escalates and the batch carries on.
 */
export const BulkApproveWithdrawsRequest = t.Object(
  {
    ids: t.Array(UUID, { minItems: 1, maxItems: 500 }),
    reason: t.String({ minLength: 1, maxLength: 500 }),
    /** Sum of the amounts the operator was shown, in minor units. */
    expectedTotalMinor: t.Optional(t.Integer({ minimum: 0 })),
  },
  { $id: 'BulkApproveWithdrawsRequest' },
);

/**
 * Deposit approve/reject.
 *
 * Deliberately NOT an extension of `ApproveRejectRequest`: that shape is shared
 * with the withdraw route, and widening it would silently let a withdraw
 * approval carry a destination account it has no code to honour.
 *
 * `houseBankAccountId` is the house account the money ACTUALLY landed in. The
 * player names one when they announce the transfer, but they can pay a
 * different one, so the approving operator gets the last word. Omitted = keep
 * whatever the row already holds; same for `channel`.
 */
export const DepositApproveRequest = t.Object(
  {
    action: t.Union([t.Literal('approve'), t.Literal('reject')]),
    /**
     * The mandatory remark. REQUIRED, and
     * required of every caller — the back office, an existing admin's script,
     * a blackbox actor. `minLength` refuses the empty string here; svc-finance
     * refuses a string of spaces (FIN-332), which TypeBox cannot express.
     *
     * The withdraw side carries the same rule on its own
     * `ApproveRejectRequest`.
     */
    reason: t.String({ minLength: 1, maxLength: 500 }),
    houseBankAccountId: t.Optional(UUID),
    channel: t.Optional(
      t.Union([t.Literal('bank_transfer'), t.Literal('promptpay')]),
    ),
  },
  { $id: 'DepositApproveRequest' },
);

export const BankMasterView = t.Object(
  {
    bankCode: t.String(),
    nameTh: t.String(),
    nameEn: t.String(),
    shortName: t.String(),
    accountNoMinLength: t.Integer(),
    accountNoMaxLength: t.Integer(),
    accountNoRegex: t.String(),
    logoUrl: t.Union([t.String(), t.Null()]),
    colorHex: t.Union([t.String(), t.Null()]),
    displayOrder: t.Integer(),
    isActive: t.Boolean(),
  },
  { $id: 'BankMasterView' },
);

export const BankAccountView = t.Object(
  {
    id: UUID,
    type: t.Union([t.Literal('deposit'), t.Literal('withdraw'), t.Literal('truewallet')]),
    bankCode: t.String(),
    accountNo: t.String(),
    accountName: t.String(),
    displayName: t.Union([t.String(), t.Null()]),
    status: t.Union([t.Literal('active'), t.Literal('inactive')]),
    autoEnabled: t.Boolean(),
    autoProvider: t.Union([
      t.Literal('web'),
      t.Literal('sms'),
      t.Literal('app'),
      t.Literal('api'),
      t.Literal('truewallet'),
      t.Literal('manual'),
    ]),
    createdAt: DateTimeISO,
  },
  { $id: 'BankAccountView' },
);
